import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { authFetch, API } from './api';

export const SUGGESTIONS = [
  'Summarize my active buyers',
  'Which buyers need attention?',
  "What's my best match right now?",
  'Which homes have the best schools?',
  'Draft an email update for a buyer',
  'What should I focus on today?',
];

// Buyers get a home-search assistant scoped to their own criteria and Match Board.
export const BUYER_SUGGESTIONS = [
  'Which home is my best match, and why?',
  'Compare my top two homes',
  'What should I look for at my next showing?',
  'Which homes have the best schools?',
  "Why didn't my top home score higher?",
  'How do my priorities affect my matches?',
];

export const CHAT_ROLES = ['super_admin', 'broker', 'agent', 'buyer'];
export const suggestionsFor = (role) => (role === 'buyer' ? BUYER_SUGGESTIONS : SUGGESTIONS);

export function renderMarkdown(text) {
  if (!text) return null;
  const lines = text.split('\n');
  const blocks = [];
  let current = null;

  for (const line of lines) {
    if (/^[-*]\s+/.test(line)) {
      if (current?.type !== 'ul') {
        if (current) blocks.push(current);
        current = { type: 'ul', items: [] };
      }
      current.items.push(line.replace(/^[-*]\s+/, ''));
    } else if (/^\d+\.\s+/.test(line)) {
      if (current?.type !== 'ol') {
        if (current) blocks.push(current);
        current = { type: 'ol', items: [] };
      }
      current.items.push(line.replace(/^\d+\.\s+/, ''));
    } else if (/^#{1,6}\s+/.test(line)) {
      // Stray markdown headers: the system prompt forbids them in chat, but if
      // the model emits one anyway, render as a bold paragraph at normal size
      // rather than a giant heading.
      if (current) {
        blocks.push(current);
        current = null;
      }
      const headerText = line.replace(/^#{1,6}\s+/, '').trim();
      blocks.push({ type: 'p', items: [`**${headerText}**`] });
    } else if (line.trim() === '') {
      if (current) {
        blocks.push(current);
        current = null;
      }
    } else {
      if (current?.type !== 'p') {
        if (current) blocks.push(current);
        current = { type: 'p', items: [] };
      }
      current.items.push(line);
    }
  }
  if (current) blocks.push(current);

  const renderInline = (s) => {
    const parts = [];
    const re = /\*\*([^*]+)\*\*/g;
    let last = 0;
    let m;
    while ((m = re.exec(s)) !== null) {
      if (m.index > last) parts.push(s.slice(last, m.index));
      parts.push(<strong key={parts.length}>{m[1]}</strong>);
      last = m.index + m[0].length;
    }
    if (last < s.length) parts.push(s.slice(last));
    return parts;
  };

  return blocks.map((b, i) => {
    if (b.type === 'ul') {
      return (
        <ul key={i} className="list-disc ml-5 my-2 space-y-0.5">
          {b.items.map((it, j) => (
            <li key={j}>{renderInline(it)}</li>
          ))}
        </ul>
      );
    }
    if (b.type === 'ol') {
      return (
        <ol key={i} className="list-decimal ml-5 my-2 space-y-0.5">
          {b.items.map((it, j) => (
            <li key={j}>{renderInline(it)}</li>
          ))}
        </ol>
      );
    }
    return (
      <p key={i} className="my-1">
        {b.items.map((line, j) => (
          <span key={j}>
            {renderInline(line)}
            {j < b.items.length - 1 && <br />}
          </span>
        ))}
      </p>
    );
  });
}

export default function ChatWidget() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [error, setError] = useState(null);

  const bottomRef = useRef(null);
  const textareaRef = useRef(null);

  // Track mobile vs desktop via matchMedia. Mobile dimensions live in inline
  // style (they depend on JS-computed visualViewport height), and inline styles
  // outrank Tailwind utilities.
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined'
      ? window.matchMedia('(max-width: 767px)').matches
      : false
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Panel height for mobile — driven by visualViewport so the input bar stays
  // visible when the iOS keyboard opens.
  const [panelHeight, setPanelHeight] = useState(
    typeof window !== 'undefined' ? window.innerHeight : 600
  );
  useEffect(() => {
    if (!open) return undefined;
    const vv = window.visualViewport;
    if (!vv) {
      setPanelHeight(window.innerHeight);
      return undefined;
    }
    const update = () => setPanelHeight(vv.height);
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, [open]);

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [messages, thinking, open]);

  // Lock the background page scroll while the chat is open. Both overflow:hidden
  // AND position:fixed are needed because iOS Safari ignores overflow:hidden on
  // the body. We freeze the body at its current scroll offset and restore on close.
  useEffect(() => {
    if (!open) return undefined;
    const body = document.body;
    const html = document.documentElement;
    const prev = {
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyWidth: body.style.width,
      htmlOverflow: html.style.overflow,
    };
    const scrollY = window.scrollY || window.pageYOffset || 0;
    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.width = '100%';
    html.style.overflow = 'hidden';
    return () => {
      body.style.overflow = prev.bodyOverflow;
      body.style.position = prev.bodyPosition;
      body.style.top = prev.bodyTop;
      body.style.width = prev.bodyWidth;
      html.style.overflow = prev.htmlOverflow;
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  if (!user || !CHAT_ROLES.includes(user.role)) {
    return null;
  }

  const sendMessage = async (text) => {
    const trimmed = (text || '').trim();
    if (!trimmed || thinking) return;

    const history = messages;
    const nextMessages = [...messages, { role: 'user', content: trimmed }];
    setMessages(nextMessages);
    setInput('');
    setThinking(true);
    setError(null);

    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    try {
      const res = await authFetch(`${API}/api/chat`, {
        method: 'POST',
        body: JSON.stringify({
          message: trimmed,
          conversation: history,
        }),
      });

      if (!res.ok) {
        throw new Error(`Request failed: ${res.status}`);
      }

      const data = await res.json();
      setMessages((m) => [
        ...m,
        { role: 'assistant', content: data.response || '' },
      ]);
    } catch (e) {
      setError('Something went wrong. Please try again.');
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          content:
            "Sorry, I couldn't process that just now. Please try again in a moment.",
        },
      ]);
    } finally {
      setThinking(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const handleInput = (e) => {
    setInput(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    const maxHeight = 24 * 4 + 16; // approx 4 rows
    el.style.height = `${Math.min(el.scrollHeight + 2, maxHeight)}px`;
  };

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => {
            // On mobile, route to a real /chat page — fixed overlays + iOS keyboards
            // are unreliable. Desktop keeps the floating-panel experience.
            if (window.innerWidth < 768) {
              navigate('/chat');
            } else {
              setOpen(true);
            }
          }}
          aria-label="Open SchemeSync AI chat"
          className="fixed bottom-6 right-6 w-14 h-14 rounded-full flex items-center justify-center text-white text-2xl shadow-lg border border-white/10 transition-all duration-200 hover:scale-105 hover:brightness-110"
          style={{ backgroundColor: '#FF6B35', zIndex: 9999 }}
        >
          <span role="img" aria-label="chat">💬</span>
        </button>
      )}

      {/* Chat panel — full-screen takeover on mobile, floating card on desktop. */}
      {open && (
      <div
        className={`fixed shadow-2xl flex flex-col overflow-hidden ${
          isMobile
            ? 'top-0 left-0 rounded-none border-0'
            : 'bottom-6 right-6 w-[400px] h-[600px] max-h-[80vh] rounded-2xl border border-[#546E7A]/40'
        }`}
        style={{
          backgroundColor: '#1A2744',
          zIndex: 9999,
          touchAction: 'manipulation',
          boxSizing: 'border-box',
          overflow: 'hidden',
          overflowX: 'hidden',
          overscrollBehavior: 'none',
          ...(isMobile && {
            width: '100vw',
            maxWidth: '100vw',
            height: `${panelHeight}px`,
          }),
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-4 py-3 border-b shrink-0"
          style={{ backgroundColor: '#1A2744', borderColor: 'rgba(84,110,122,0.3)' }}
        >
          <div className="flex items-center gap-2">
            <span
              className="w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: '#FF6B35' }}
            />
            <div className="flex flex-col leading-tight">
              <span className="text-white font-semibold text-sm">SchemeSync AI</span>
              <span className="text-gray-500 text-xs">
                {user.role === 'buyer' ? 'Ask anything about your matched homes' : 'Ask anything about your buyers & homes'}
              </span>
            </div>
          </div>
          <button
            onClick={() => setOpen(false)}
            aria-label="Close chat"
            className="text-gray-400 hover:text-white leading-none flex items-center justify-center w-11 h-11 md:w-8 md:h-8 text-2xl md:text-lg shrink-0"
          >
            ✕
          </button>
        </div>

        {/* Messages */}
        <div
          className="flex-1 overflow-y-auto overscroll-contain p-4 space-y-3"
          style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}
        >
          {messages.length === 0 && (
            <div className="flex flex-col md:flex-row md:flex-wrap gap-2 w-full max-w-full">
              {suggestionsFor(user.role).map((s) => (
                <button
                  key={s}
                  onClick={() => sendMessage(s)}
                  className="w-full md:w-auto text-left md:text-center px-3 py-2 md:py-1.5 rounded-lg md:rounded-full text-xs border transition-colors hover:border-[#00D4FF] truncate min-w-0"
                  style={{
                    backgroundColor: 'rgba(10,14,39,0.7)',
                    borderColor: 'rgba(0,212,255,0.3)',
                    color: '#00D4FF',
                    maxWidth: '100%',
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {messages.map((msg, i) =>
            msg.role === 'user' ? (
              <div
                key={i}
                className="ml-auto max-w-[85%] px-3 py-2 rounded-2xl rounded-tr-sm border text-white text-sm bg-[#FF6B35]/15 border-[#FF6B35]/30"
              >
                {msg.content}
              </div>
            ) : (
              <div
                key={i}
                className="mr-auto max-w-[90%] px-3 py-2 rounded-2xl rounded-tl-sm border text-gray-200 text-sm bg-[#0A0E27] border-[#546E7A]/30"
              >
                {renderMarkdown(msg.content)}
              </div>
            )
          )}

          {thinking && (
            <div className="mr-auto max-w-[90%] px-3 py-2 rounded-2xl rounded-tl-sm border text-gray-200 text-sm bg-[#0A0E27] border-[#546E7A]/30">
              <span className="inline-flex gap-0.5">
                <span className="animate-bounce" style={{ animationDelay: '0ms' }}>.</span>
                <span className="animate-bounce" style={{ animationDelay: '150ms' }}>.</span>
                <span className="animate-bounce" style={{ animationDelay: '300ms' }}>.</span>
              </span>
            </div>
          )}

          <div ref={bottomRef} />
        </div>

        {error && (
          <div className="px-4 py-1.5 text-xs text-red-300 bg-red-900/20 border-t border-red-500/20">
            {error}
          </div>
        )}

        {/* Input bar */}
        <div
          className="px-3 py-2 flex items-end gap-2 border-t shrink-0 w-full max-w-full"
          style={{ borderColor: 'rgba(84,110,122,0.3)', backgroundColor: '#1A2744', touchAction: 'auto', boxSizing: 'border-box' }}
        >
          <textarea
            ref={textareaRef}
            rows={1}
            value={input}
            onChange={handleInput}
            onKeyDown={handleKeyDown}
            placeholder="Ask anything..."
            className="flex-1 min-w-0 resize-none rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 border focus:outline-none focus:border-[#00D4FF]/60"
            style={{
              backgroundColor: '#0A0E27',
              borderColor: 'rgba(84,110,122,0.5)',
              maxHeight: '112px',
            }}
          />
          <button
            onClick={() => sendMessage(input)}
            disabled={!input.trim() || thinking}
            aria-label="Send"
            className="w-11 h-11 md:w-9 md:h-9 shrink-0 rounded-full md:rounded-lg flex items-center justify-center text-white text-base transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110"
            style={{ backgroundColor: '#FF6B35' }}
          >
            <span>➤</span>
          </button>
        </div>
      </div>
      )}
    </>
  );
}
